import React, { useState, useEffect } from "react";
import Select from "react-select";
import { filterLessonsBySubjectUnit } from "../ConsolidatedReportsUtilities";
import {
  getLessonsForStandardSet,
  extractAvailableStandardSets,
  getStandardSetDisplayName,
  normalizeStandardSet,
} from "../../../common/standardSetUtils";

const ReportsGroupedLessonSelect = ({
  lessons,
  changeHandler,
  selectedLesson,
  activeSubject,
  activeUnit,
  ready,
  preferredStandardSet,
}) => {
  const [activeGroup, setActiveGroup] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Initialize expandedStandardsets with preferred standard set auto-expanded
  const [expandedStandardsets, setExpandedStandardsets] = useState(() => {
    const initialExpanded = new Set();

    // Auto-expand preferred standard set if we have it
    if (preferredStandardSet) {
      const normalizedPreferred = normalizeStandardSet(preferredStandardSet);
      initialExpanded.add(normalizedPreferred);
    }

    return initialExpanded;
  });

  // Update expandedStandardsets when data changes (must be before early return)
  useEffect(() => {
    if (!lessons || !preferredStandardSet) return;

    const filteredLessons = filterLessonsBySubjectUnit(
      lessons,
      activeSubject,
      activeUnit
    );

    const availableStandardsets = extractAvailableStandardSets(filteredLessons);

    if (availableStandardsets.length > 0) {
      const normalizedPreferred = normalizeStandardSet(preferredStandardSet);
      const preferredStandardExists = availableStandardsets.some(
        (standardset) => standardset.shortname === normalizedPreferred
      );

      if (preferredStandardExists) {
        setExpandedStandardsets(
          (prev) => new Set([...prev, normalizedPreferred])
        );
      }
    }
  }, [lessons, activeSubject, activeUnit, preferredStandardSet]);

  if (!lessons) return null;

  const filteredLessons = filterLessonsBySubjectUnit(
    lessons,
    activeSubject,
    activeUnit
  );

  const availableStandardsets = extractAvailableStandardSets(
    filteredLessons
  ).sort((a, b) => {
    // Dynamic sorting using preferredStandardSet
    if (preferredStandardSet) {
      const preferredDisplayName =
        getStandardSetDisplayName(preferredStandardSet);

      // Put preferred standard set first
      if (a.displayName === preferredDisplayName) return -1;
      if (b.displayName === preferredDisplayName) return 1;
    }

    // Put National Standards second (if not already preferred)
    const nationalDisplayName = "National Standards";
    if (preferredStandardSet !== "CCSS") {
      if (
        a.displayName === nationalDisplayName &&
        b.displayName !== nationalDisplayName
      )
        return -1;
      if (
        b.displayName === nationalDisplayName &&
        a.displayName !== nationalDisplayName
      )
        return 1;
    }

    // All others alphabetically
    return a.displayName.localeCompare(b.displayName);
  });

  const groupStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    fontSize: 16,
    color: "#666",
    fontWeight: "bold",
  };

  const groupBadgeStyles = {
    backgroundColor: "#EBECF0",
    borderRadius: "2em",
    color: "#172B4D",
    display: "inline-block",
    fontSize: 12,
    fontWeight: "normal",
    lineHeight: "1",
    minWidth: 1,
    padding: "0.16666666666667em 0.5em",
    textAlign: "center",
  };

  const standardsetBadgeStyles = {
    ...groupBadgeStyles,
    backgroundColor: "#EBECF0",
    color: "#172B4D",
  };

  const toggleStandardsetExpansion = (shortname) => {
    const newExpanded = new Set(expandedStandardsets);
    if (newExpanded.has(shortname)) {
      newExpanded.delete(shortname);
    } else {
      newExpanded.add(shortname);
    }
    setExpandedStandardsets(newExpanded);
  };

  const createNestedOptions = () => {
    const options = [];

    availableStandardsets.forEach((standardset) => {
      // Standard set header with lesson count badge
      options.push({
        value: `standardset_header_${standardset.shortname}`,
        label: standardset.displayName,
        lessonCount: standardset.lessonCount,
        isStandardsetHeader: true,
        standardsetShortname: standardset.shortname,
        isDisabled: false,
      });

      if (expandedStandardsets.has(standardset.shortname)) {
        const lessonGroups = getLessonsForStandardSet(
          filteredLessons,
          standardset.shortname
        );

        lessonGroups.forEach((group) => {
          // Unit group header
          options.push({
            value: `group_header_${standardset.shortname}_${group.label}`,
            label: `    ${group.label}`,
            isGroupHeader: true,
            groupLabel: group.label,
            standardsetShortname: standardset.shortname,
            isDisabled: true,
          });

          // Individual lessons
          group.options.forEach((lesson) => {
            options.push({
              ...lesson,
              label: `        ${lesson.label}`,
              isLesson: true,
              originalLabel: lesson.label,
              standardsetShortname: standardset.shortname,
              group: group.label,
            });
          });
        });
      }
    });

    return options;
  };

  const all_options = createNestedOptions();

  const getCurrentValue = () => {
    if (selectedLesson) {
      for (const standardset of availableStandardsets) {
        const standardsetLessons = getLessonsForStandardSet(
          filteredLessons,
          standardset.shortname
        );
        for (const group of standardsetLessons) {
          const foundLesson = group.options.find(
            (option) => option.value === selectedLesson
          );
          if (foundLesson) {
            const labelParts = foundLesson.label.split(" - ");
            const cleanLabel =
              labelParts.length >= 2
                ? `${labelParts[0]} - ${labelParts[1]}`
                : foundLesson.label;

            return {
              ...foundLesson,
              label: cleanLabel,
              isLesson: true,
              originalLabel: foundLesson.label,
            };
          }
        }
      }
    }
    return null;
  };

  const getPlaceholder = () => {
    if (availableStandardsets.length > 1) {
      return "Select standard set, then lesson";
    }
    return "Select a lesson";
  };

  const handleOptionSelect = (selected) => {
    if (selected.isStandardsetHeader) {
      toggleStandardsetExpansion(selected.standardsetShortname);
      return;
    } else if (selected.isGroupHeader) {
      return;
    } else if (selected.isLesson) {
      changeHandler(selected.value, selected);
      setIsDropdownOpen(false);
    }
  };

  const formatGroupLabel = (data) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: "pointer",
        fontSize: 16,
        color: "#666",
        fontWeight: "bold",
      }}
      onClick={() => {
        setActiveGroup(activeGroup === data.label ? "" : data.label);
      }}
    >
      <span>{data.label}</span>
      <span
        style={{
          backgroundColor: "#EBECF0",
          borderRadius: "2em",
          color: "#172B4D",
          display: "inline-block",
          fontSize: 12,
          fontWeight: "normal",
          padding: "0.17em 0.5em",
        }}
      >
        {data.options.length}
      </span>
    </div>
  );

  const formatOptionLabel = (data, { context }) => {
    if (data.isStandardsetHeader) {
      return (
        <div style={groupStyles}>
          <span>{data.label}</span>
          <span style={standardsetBadgeStyles}>{data.lessonCount}</span>
        </div>
      );
    }

    if (
      data.isLesson &&
      data.standard &&
      data.standard !== "N/A" &&
      context === "menu"
    ) {
      return (
        <div>
          <div>{data.originalLabel || data.label}</div>
        </div>
      );
    }

    return data.originalLabel || data.label;
  };

  const customStyles = {
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      let backgroundColor = styles.backgroundColor;
      let color = styles.color;
      let fontWeight = "normal";
      let padding = "8px 12px";

      const isCurrentlySelected =
        data.isLesson && data.value === selectedLesson;

      if (data.isStandardsetHeader) {
        backgroundColor = "#ffffff";
        color = "#666";
        fontWeight = "bold";
      } else if (data.isGroupHeader) {
        backgroundColor = "#f8f9fa";
        color = "#666";
        fontWeight = "600";
        padding = "6px 12px";
      } else if (data.isLesson) {
        padding = "8px 12px 8px 24px";

        if (isCurrentlySelected) {
          backgroundColor = "#2684FF";
          color = "#fff";
        } else if (isFocused) {
          backgroundColor = "#DFEBFF";
        }

        return {
          ...styles,
          backgroundColor,
          color,
          fontWeight,
          padding,
          cursor: isDisabled ? "default" : "pointer",
          display:
            !activeGroup ||
            activeGroup.toUpperCase() === data.group?.toUpperCase()
              ? "block"
              : "none",
        };
      }

      return {
        ...styles,
        backgroundColor,
        color,
        fontWeight,
        padding,
        cursor: isDisabled ? "default" : "pointer",
      };
    },
    menuList: (styles) => ({
      ...styles,
      padding: 0,
    }),
    menu: (styles) => ({
      ...styles,
      zIndex: 999,
    }),
  };

  return (
    <Select
      aria-label='Select a lesson'
      isSearchable={false}
      formatGroupLabel={formatGroupLabel}
      formatOptionLabel={formatOptionLabel}
      options={all_options}
      styles={customStyles}
      isDisabled={!ready}
      onChange={handleOptionSelect}
      value={getCurrentValue()}
      placeholder={getPlaceholder()}
      menuIsOpen={isDropdownOpen}
      onMenuOpen={() => setIsDropdownOpen(true)}
      onMenuClose={() => setIsDropdownOpen(false)}
      closeMenuOnSelect={false}
      isOptionDisabled={(option) => option.isGroupHeader}
    />
  );
};

export default ReportsGroupedLessonSelect;
